import { useEffect, useRef, useState } from "react";

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const BOKEH_COLORS = [
  [255, 176, 92], [255, 120, 60], [96, 150, 255], [46, 107, 255],
  [80, 220, 200], [255, 214, 150], [255, 70, 60],
];

/* Time-of-day palettes — the backdrop runs on the LIVE server clock */
const PALETTES = {
  night: { sky: ["#020409", "#050a14", "#0a0f1c", "#060407"], glow: ["rgba(70,110,190,0.10)", "rgba(255,140,60,0.07)"], orbMul: 1.0, amb: 0 },
  dawn:  { sky: ["#0a0d1c", "#1a1f38", "#4a3040", "#1a0f14"], glow: ["rgba(255,150,90,0.16)", "rgba(255,190,120,0.12)"], orbMul: 0.85, amb: 0.1 },
  day:   { sky: ["#0e1a2e", "#16263f", "#23405e", "#0d1420"], glow: ["rgba(140,180,255,0.14)", "rgba(200,220,255,0.08)"], orbMul: 0.45, amb: 0.22 },
  dusk:  { sky: ["#120a20", "#2c1630", "#6b2f2a", "#140a10"], glow: ["rgba(255,120,80,0.18)", "rgba(255,90,60,0.10)"], orbMul: 0.8, amb: 0.08 },
};
const paletteFor = (h) => (h == null ? PALETTES.night : h >= 5 && h < 7 ? PALETTES.dawn : h >= 7 && h < 17 ? PALETTES.day : h >= 17 && h < 20 ? PALETTES.dusk : PALETTES.night);

/* Server soul — collective behaviour repaints the whole menu */
const SOUL_TINTS = { lawful: "rgba(46,107,255,0.10)", chaotic: "rgba(239,68,68,0.12)", corrupt: "rgba(190,60,220,0.11)", heroic: "rgba(232,185,62,0.08)", neutral: null };

export const DEAD_DROP_POS = { x: 0.62, y: 0.735 }; // briefcase glow + DOM button share this spot

/* Living World menu backdrop — the canvas reads /nc/menu/world through a ref:
   population drives light density + traffic, server clock drives palette, real or
   server weather drives precipitation, crime mood drives the red horizon + sirens,
   server soul tints the grade, ghost walkers show real players online, the
   obituary wall is carved bottom-left, and the dead-drop glow pulses for the
   recipient. Cinematic video layer + camera drift preserved. */
export function CityBackdrop({ accent = "#2E6BFF", zoomRef, dimRef, world = null, ghosts = 0 }) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const [videoOk, setVideoOk] = useState(true);
  const worldRef = useRef(world);
  worldRef.current = world;
  const ghostRef = useRef(ghosts);
  ghostRef.current = ghosts;

  useEffect(() => {
    if (!videoOk) return;
    let raf;
    const tickFn = () => {
      const z = zoomRef?.current ?? 1;
      if (videoRef.current) videoRef.current.style.transform = `scale(${1.08 * z})`;
      raf = requestAnimationFrame(tickFn);
    };
    raf = requestAnimationFrame(tickFn);
    return () => cancelAnimationFrame(raf);
  }, [videoOk, zoomRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, W, H;
    let orbs = [], drops = [], flakes = [], cars = [], walkers = [], fogs = [];
    let flare = { active: false, nextAt: 6, x: 0, y: 0, t: 0 };
    let siren = { nextAt: 3, x: 0, t: -10 };
    let lightning = { nextAt: 8, t: -10 };
    let witness = { nextAt: 7, x: 0, t: -20 };   // the Witness — fleeting, only mine
    const mouse = { x: -9999, y: -9999 };
    const onMouse = (e) => { const r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    window.addEventListener("mousemove", onMouse);

    const build = () => {
      const rand = mulberry32(2019700);
      const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
      W = canvas.clientWidth || 1920; H = canvas.clientHeight || 1080;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const w = worldRef.current;
      const density = 0.35 + (w?.city?.population ?? 0.3) * 1.3; // living world: more players, more city
      const orbCount = Math.round(60 * density + 30);
      orbs = [];
      for (let i = 0; i < orbCount; i++) {
        const band = rand();
        const y = band < 0.55 ? H * (0.62 + rand() * 0.3) : H * (0.12 + rand() * 0.5);
        const c = BOKEH_COLORS[(rand() * BOKEH_COLORS.length) | 0];
        orbs.push({ x: rand() * W, y, r: 14 + rand() * (y > H * 0.6 ? 90 : 46), c, a: 0.05 + rand() * 0.22,
                    dx: (rand() - 0.5) * 0.22, dy: (rand() - 0.5) * 0.1, tw: rand() * 6.28, tws: 0.3 + rand() * 1.2 });
      }
      // traffic — headlight/taillight streaks, density follows population
      cars = Array.from({ length: Math.round(4 + density * 14) }, () => ({
        y: H * (0.8 + rand() * 0.1), x: rand() * W, s: (1.5 + rand() * 3) * (rand() < 0.5 ? 1 : -1),
        warm: rand() < 0.55, len: 30 + rand() * 50 }));
      drops = Array.from({ length: 170 }, () => ({ x: rand() * W, y: rand() * H, l: 9 + rand() * 15, s: 10 + rand() * 9, a: 0.04 + rand() * 0.1 }));
      flakes = Array.from({ length: 90 }, () => ({ x: rand() * W, y: rand() * H, r: 1 + rand() * 2.2, s: 0.5 + rand() * 1.1, ph: rand() * 6.28 }));
      fogs = Array.from({ length: 5 }, (_, i) => ({ y: H * (0.3 + i * 0.14), x: rand() * W, w: W * (0.5 + rand() * 0.6), s: 0.12 + rand() * 0.2, a: 0.05 + rand() * 0.05 }));
      walkers = Array.from({ length: 16 }, () => ({ x: rand() * W, y: H * (0.84 + rand() * 0.06), s: (0.3 + rand() * 0.6) * (rand() < 0.5 ? 1 : -1), h: 16 + rand() * 14, ph: rand() * 6.28 }));
    };

    build();
    const onResize = () => build();
    window.addEventListener("resize", onResize);

    const t0 = performance.now();
    const frame = (now) => {
      const t = (now - t0) / 1000;
      const w = worldRef.current;
      const city = w?.city || {};
      const pal = paletteFor(city.time_hour);
      const crime = city.crime ?? 0;
      const weather = (city.weather_real?.kind || city.weather_server || "clear").toLowerCase();
      const soul = SOUL_TINTS[city.soul?.dominant] || null;
      const z = zoomRef?.current ?? 1;
      ctx.clearRect(0, 0, W, H);

      // sky — server-clock palette, reddened by the crime mood
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, pal.sky[0]); sky.addColorStop(0.55, pal.sky[1]);
      sky.addColorStop(0.8, pal.sky[2]); sky.addColorStop(1, pal.sky[3]);
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
      if (crime > 0.25) { // atmospheric crime weather — the sky itself turns on bad nights
        const rg = ctx.createLinearGradient(0, H * 0.35, 0, H * 0.85);
        rg.addColorStop(0, "transparent");
        rg.addColorStop(1, `rgba(190,30,40,${0.05 + crime * 0.14})`);
        ctx.fillStyle = rg; ctx.fillRect(0, H * 0.35, W, H * 0.5);
      }
      if (pal.amb > 0) { ctx.fillStyle = `rgba(120,160,220,${pal.amb * 0.14})`; ctx.fillRect(0, 0, W, H); }

      const horizon = ctx.createLinearGradient(0, H * 0.55, 0, H * 0.8);
      horizon.addColorStop(0, "transparent");
      horizon.addColorStop(0.7, pal.glow[0]);
      horizon.addColorStop(1, pal.glow[1]);
      ctx.fillStyle = horizon;
      ctx.fillRect(0, H * 0.55, W, H * 0.25);

      ctx.save();
      ctx.translate(W / 2, H / 2); ctx.scale(z, z); ctx.translate(-W / 2, -H / 2);

      // fog banks drift behind everything when the real/server weather says so
      if (weather === "fog" || weather === "cloudy") {
        const mul = weather === "fog" ? 1.6 : 0.7;
        for (const f of fogs) {
          f.x += f.s; if (f.x - f.w > W) f.x = -f.w;
          const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.w / 2);
          g.addColorStop(0, `rgba(180,195,215,${f.a * mul})`); g.addColorStop(1, "transparent");
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.ellipse(f.x, f.y, f.w / 2, H * 0.07, 0, 0, 6.2832); ctx.fill();
        }
      }

      // bokeh field (population-scaled), dimmed by fog
      const fogMul = weather === "fog" ? 0.55 : 1;
      for (const o of orbs) {
        o.x += o.dx; o.y += o.dy;
        if (o.x < -120) o.x = W + 120; if (o.x > W + 120) o.x = -120;
        if (o.y < -120) o.y = H + 120; if (o.y > H + 120) o.y = -120;
        const tw = 0.65 + 0.35 * Math.sin(t * o.tws + o.tw);
        const a = o.a * tw * pal.orbMul * fogMul * (w?.me?.absence?.abandoned ? 0.4 : 1);
        const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        g.addColorStop(0, `rgba(${o.c[0]},${o.c[1]},${o.c[2]},${a})`);
        g.addColorStop(0.55, `rgba(${o.c[0]},${o.c[1]},${o.c[2]},${a * 0.45})`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(o.x, o.y, o.r, 0, 6.2832); ctx.fill();
        if (o.r > 60) {
          ctx.fillStyle = `rgba(${o.c[0]},${o.c[1]},${o.c[2]},${a * 0.8})`;
          ctx.beginPath(); ctx.arc(o.x, o.y, o.r * 0.16, 0, 6.2832); ctx.fill();
        }
      }

      // traffic streaks — the busier the server, the busier the street
      for (const c of cars) {
        c.x += c.s * (1 + (city.population ?? 0.3));
        if (c.s > 0 && c.x - c.len > W) c.x = -c.len;
        if (c.s < 0 && c.x + c.len < 0) c.x = W + c.len;
        const col = c.warm ? "255,214,150" : "255,70,60";
        const g = ctx.createLinearGradient(c.x - c.len, 0, c.x, 0);
        g.addColorStop(0, "transparent"); g.addColorStop(1, `rgba(${col},0.35)`);
        ctx.fillStyle = g;
        ctx.fillRect(c.x - c.len, c.y, c.len, 2.5);
      }

      // siren pulses on high-crime nights — alternating red/blue at the horizon
      if (crime > 0.35) {
        if (t > siren.nextAt) siren = { nextAt: t + 3.5 + Math.random() * 4, x: W * (0.15 + Math.random() * 0.7), t };
        const p = (t - siren.t) / 2.4;
        if (p < 1) {
          const flip = Math.floor(t * 3) % 2 === 0;
          const col = flip ? "255,50,60" : "70,120,255";
          const g = ctx.createRadialGradient(siren.x, H * 0.72, 0, siren.x, H * 0.72, W * 0.16);
          g.addColorStop(0, `rgba(${col},${(1 - p) * 0.16 * crime})`); g.addColorStop(1, "transparent");
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(siren.x, H * 0.72, W * 0.16, 0, 6.2832); ctx.fill();
        }
      }

      // ghost players — silhouettes of citizens online right now, walking the street
      const nGhosts = Math.min(walkers.length, ghostRef.current || 0);
      for (let i = 0; i < nGhosts; i++) {
        const gk = walkers[i];
        gk.x += gk.s; if (gk.s > 0 && gk.x > W + 30) gk.x = -30; if (gk.s < 0 && gk.x < -30) gk.x = W + 30;
        const bob = Math.sin(t * 6 + gk.ph) * 1.5;
        ctx.fillStyle = "rgba(2,4,8,0.85)";
        ctx.strokeStyle = `${accent}55`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(gk.x, gk.y - gk.h + bob, gk.h * 0.22, 0, 6.2832); ctx.fill(); ctx.stroke();
        ctx.beginPath();
        ctx.roundRect(gk.x - gk.h * 0.2, gk.y - gk.h * 0.78 + bob, gk.h * 0.4, gk.h * 0.78, gk.h * 0.16);
        ctx.fill(); ctx.stroke();
      }

      // the Witness — the perpetrator from your unsolved case. Fleeting: gone the
      // moment your cursor turns toward it. Only rendered on YOUR menu.
      if (w?.me?.witness) {
        if (t > witness.nextAt) witness = { nextAt: t + 9 + Math.random() * 7, x: W * (0.1 + Math.random() * 0.8), t };
        const p = (t - witness.t) / 3.2;
        const nearMouse = Math.abs(mouse.x - witness.x) < 140 && Math.abs(mouse.y - H * 0.62) < 200;
        if (p < 1 && !nearMouse) {
          const a = Math.sin(p * Math.PI) * 0.75; // fade in, hold, fade out
          const wy = H * 0.68, wh2 = 74;
          ctx.fillStyle = `rgba(1,2,5,${a})`;
          ctx.beginPath(); ctx.arc(witness.x, wy - wh2, wh2 * 0.2, 0, 6.2832); ctx.fill();
          ctx.beginPath(); ctx.roundRect(witness.x - wh2 * 0.19, wy - wh2 * 0.8, wh2 * 0.38, wh2 * 0.8, wh2 * 0.15); ctx.fill();
          ctx.fillStyle = `rgba(120,10,20,${a * 0.5})`; // the faintest red rim — something's wrong with this one
          ctx.fillRect(witness.x - wh2 * 0.19, wy - wh2 * 0.8, 1.5, wh2 * 0.8);
        } else if (nearMouse) {
          witness.nextAt = t + 9 + Math.random() * 7; witness.t = -20; // looked straight at it — gone
        }
      }

      // wet asphalt + mirrored smears
      const road = ctx.createLinearGradient(0, H * 0.86, 0, H);
      road.addColorStop(0, "rgba(4,5,8,0)"); road.addColorStop(0.4, "rgba(4,5,8,0.85)"); road.addColorStop(1, "#020203");
      ctx.fillStyle = road; ctx.fillRect(0, H * 0.86, W, H * 0.14);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (const o of orbs) {
        if (o.y < H * 0.62 || o.y > H * 0.92) continue;
        const ry = H * 0.9 + (o.y - H * 0.86) * 0.35;
        const g = ctx.createLinearGradient(o.x, ry, o.x, ry + o.r * 2.4);
        g.addColorStop(0, `rgba(${o.c[0]},${o.c[1]},${o.c[2]},${o.a * 0.16 * pal.orbMul})`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.fillRect(o.x - o.r * 0.24, ry, o.r * 0.48, o.r * 2.4);
      }
      ctx.restore();

      // the obituary wall — permadeath names carved into granite, candle glow at the base
      const obits = w?.obituaries || [];
      if (obits.length) {
        const wx = W * 0.03, wy = H * 0.5, ww = 250, wh = 120 + Math.min(6, obits.length) * 15;
        ctx.fillStyle = "rgba(10,10,12,0.88)";
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.beginPath(); ctx.roundRect(wx, wy, ww, wh, 6); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = "700 10px 'JetBrains Mono', monospace";
        ctx.fillText("IN MEMORY — THE PERMANENTLY FALLEN", wx + 14, wy + 22);
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.font = "10px 'JetBrains Mono', monospace";
        obits.slice(0, 8).forEach((o, i) => ctx.fillText(`† ${o.name}${o.cause ? ` — ${o.cause}` : ""}`.slice(0, 38), wx + 14, wy + 42 + i * 15));
        for (let i = 0; i < Math.min(5, obits.length); i++) {
          const cx = wx + 20 + i * 46, cy = wy + wh - 12;
          const fl = 0.5 + 0.5 * Math.sin(t * 7 + i * 2.1);
          const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 9);
          g.addColorStop(0, `rgba(255,190,90,${0.5 + fl * 0.4})`); g.addColorStop(1, "transparent");
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, 9, 0, 6.2832); ctx.fill();
        }
      }

      // dead-drop glow — a briefcase is waiting somewhere in the city (DOM button sits here)
      if (w?.me?.dead_drops > 0) {
        const bx = W * DEAD_DROP_POS.x, by = H * DEAD_DROP_POS.y;
        const pulse = 0.5 + 0.5 * Math.sin(t * 2.4);
        const g = ctx.createRadialGradient(bx, by, 0, bx, by, 60 + pulse * 30);
        g.addColorStop(0, `rgba(232,185,62,${0.2 + pulse * 0.25})`); g.addColorStop(1, "transparent");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(bx, by, 60 + pulse * 30, 0, 6.2832); ctx.fill();
      }

      // anamorphic flare sweep
      if (!flare.active && t > flare.nextAt) flare = { active: true, t, x: W * (0.25 + Math.random() * 0.5), y: H * (0.2 + Math.random() * 0.35), nextAt: t + 14 + Math.random() * 10 };
      if (flare.active) {
        const p = (t - flare.t) / 2.6;
        if (p > 1) flare.active = false;
        else {
          const fa = Math.sin(p * Math.PI) * 0.2;
          const fg = ctx.createLinearGradient(flare.x - W * 0.3, 0, flare.x + W * 0.3, 0);
          fg.addColorStop(0, "transparent"); fg.addColorStop(0.5, `rgba(140,190,255,${fa})`); fg.addColorStop(1, "transparent");
          ctx.fillStyle = fg; ctx.fillRect(flare.x - W * 0.3, flare.y - 1.5, W * 0.6, 3);
        }
      }

      // precipitation — real-world weather first, server weather as fallback
      if (weather.includes("rain") || weather === "storm" || weather === "drizzle") {
        const mul = weather === "storm" ? 2.1 : 1;
        ctx.lineWidth = 1;
        drops.forEach((d) => {
          d.y += d.s * mul; d.x -= d.s * 0.16;
          if (d.y > H) { d.y = -20; d.x = Math.random() * (W + 80); }
          ctx.strokeStyle = `rgba(170,195,235,${d.a * mul})`;
          ctx.beginPath(); ctx.moveTo(d.x, d.y); ctx.lineTo(d.x - d.l * 0.16, d.y + d.l); ctx.stroke();
        });
        if (weather === "storm") { // lightning
          if (t > lightning.nextAt) lightning = { nextAt: t + 6 + Math.random() * 9, t };
          const p = (t - lightning.t) / 0.5;
          if (p < 1) { ctx.fillStyle = `rgba(210,225,255,${(1 - p) * 0.22})`; ctx.fillRect(0, 0, W, H); }
        }
      } else if (weather === "snow") {
        ctx.fillStyle = "rgba(235,242,255,0.5)";
        for (const f of flakes) {
          f.y += f.s; f.x += Math.sin(t + f.ph) * 0.4;
          if (f.y > H) { f.y = -6; f.x = Math.random() * W; }
          ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, 6.2832); ctx.fill();
        }
      }
      ctx.restore();

      // server soul grade + chaotic instability flicker
      if (soul) {
        const flicker = city.soul?.dominant === "chaotic" ? 0.75 + 0.25 * Math.sin(t * 9) * Math.sin(t * 3.7) : 1;
        ctx.fillStyle = soul;
        ctx.globalAlpha = flicker;
        ctx.fillRect(0, 0, W, H);
        ctx.globalAlpha = 1;
      }

      // biometric decay — gone 30+ days and the city moved on without you
      if (w?.me?.absence?.abandoned) {
        ctx.fillStyle = "rgba(6,10,6,0.38)"; // drained, overgrown grade
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = "rgba(60,110,50,0.5)"; // vines crawling the street line
        ctx.lineWidth = 2;
        for (let i = 0; i < 12; i++) {
          const vx = (i / 12) * W + Math.sin(i * 7) * 40;
          ctx.beginPath();
          ctx.moveTo(vx, H);
          ctx.quadraticCurveTo(vx + 14 * Math.sin(t * 0.4 + i), H - 60 - (i % 4) * 26, vx + 4, H - 110 - (i % 3) * 30);
          ctx.stroke();
        }
      }

      const dim = dimRef?.current ?? 0;
      if (dim > 0) { ctx.fillStyle = `rgba(0,0,0,${dim})`; ctx.fillRect(0, 0, W, H); }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); window.removeEventListener("mousemove", onMouse); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accent, zoomRef, dimRef]);

  // rebuild the light field when population bucket changes materially
  const popBucket = Math.round((world?.city?.population ?? 0.3) * 5);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) canvas.dispatchEvent(new Event("resize"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popBucket]);

  return (
    <>
      <canvas data-testid="menu-city-backdrop" ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />
      {videoOk && (
        <video
          data-testid="menu-cinematic-video"
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ filter: "blur(9px) brightness(0.62) saturate(1.25) contrast(1.06)", opacity: 0.95, transition: "transform 0.2s linear" }}
          autoPlay muted loop playsInline preload="auto"
          onError={(e) => {
            const code = e.currentTarget.error?.code;
            if (code === 1) {
              const v = e.currentTarget;
              setTimeout(() => { try { v.load(); v.play().catch(() => {}); } catch {} }, 400);
              return;
            }
            setVideoOk(false);
          }}
        >
          <source src="/assets/menu-cinematic.webm" type="video/webm" />
          <source src="/assets/menu-cinematic.mp4" type="video/mp4" />
        </video>
      )}
    </>
  );
}
